import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MARKETING_CTAS } from '@/lib/marketingCtas';
import { MantaRayIcon } from '@/components/icons/MantaRayIcon';
import { Heart, Target, Eye } from 'lucide-react';

const VALUES = [
  {
    icon: Target,
    title: 'Expert, Vetted Tutors',
    description:
      'Every tutor on Mentara is personally reviewed. We don\'t scale by lowering the bar. Qualified, expert tutors only.',
  },
  {
    icon: Eye,
    title: 'Full Transparency',
    description:
      'Students see feedback. Parents see progress. Tutors see their impact. Everyone stays informed.',
  },
  {
    icon: Heart,
    title: 'Student-First',
    description:
      'Every feature we build starts with one question: does this help students learn better?',
  },
];

export function AboutPage() {
  return (
    <>
      <title>About Mentara | Online Tutoring Platform</title>
      <meta name="description" content="Mentara is an online tutoring platform connecting students with expert tutors. Learn our story, mission, and commitment to personalized education." />
      <meta name="keywords" content="about mentara, online tutoring platform, personalized tutoring, tutoring company" />
      <link rel="canonical" href="https://mentaratutors.com/about/" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mentaratutors.com/about/" />
      <meta property="og:title" content="About Mentara | Online Tutoring Platform" />
      <meta property="og:description" content="Mentara is an online tutoring platform connecting students with expert tutors. Learn our story, mission, and commitment to personalized education." />
      <meta property="og:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content="https://mentaratutors.com/about/" />
      <meta property="twitter:title" content="About Mentara | Online Tutoring Platform" />
      <meta property="twitter:description" content="Mentara is an online tutoring platform connecting students with expert tutors. Learn our story, mission, and commitment to personalized education." />
      <meta property="twitter:image" content="https://mentaratutors.com/logos/mentara-full-light.png" />

      <div className="bg-background">
      {/* Hero */}
      <section className="py-20 md:py-28 px-4">
        <div className="container max-w-3xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <MantaRayIcon className="h-16 w-16 animate-swim" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl leading-tight">
            About Mentara
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
            We believe every student deserves access to personalized online tutoring, and every tutor deserves tools
            that let them focus on what they do best: teaching.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 px-4 border-t border-border/40">
        <div className="container max-w-3xl mx-auto space-y-6">
          <h2 className="text-3xl font-bold tracking-tight">Our Story</h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Mentara started when a group of students saw the same problem
              over and over: tutoring was fragmented. Students found tutors through word of mouth.
              Scheduling happened over text. Payments were awkward. Parents had no idea what was going on.
            </p>
            <p>
              We built Mentara to fix that. One online tutoring platform where students can find verified tutors, schedule
              flexible sessions, join video calls, get feedback, and track progress, and where parents and tutors
              have the tools they need too.
            </p>
            <p>
              Today, Mentara serves students across multiple subjects: math tutoring, science, SAT prep, homework help,
              and more. We're still growing, still improving, and still obsessed with helping students improve grades,
              build confidence, and reach their goals.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 border-t border-border/40">
        <div className="container max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold tracking-tight mb-10">What We Believe</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {VALUES.map(({ icon: Icon, title, description }) => (
              <div key={title} className="space-y-3">
                <div className="inline-flex items-center justify-center rounded-full bg-primary/10 p-3">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 border-t border-border/40">
        <div className="container max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-3xl font-bold tracking-tight">Join us</h2>
          <p className="text-lg text-muted-foreground">
            Whether you're a student, parent, or tutor, we'd love to have you.
          </p>
          <Button size="lg" asChild>
            <Link to={MARKETING_CTAS.closeCourse.href}>
              {MARKETING_CTAS.closeCourse.label}
            </Link>
          </Button>
        </div>
      </section>
    </div>
    </>
  );
}
